import React, { memo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import CheckOutlineIcon from 'transfix-icons/build/CheckOutlineIcon'
import RightArrowIcon from 'transfix-icons/build/RightArrowIcon'
import Button from 'transfix-ui/components/Button'
import Card from 'transfix-ui/components/Card'
import { Typography } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { brandColors } from 'transfix-ui/theme/colors'
import SetupGuideTruckIllustration from 'apps/fleet-planner/components/illustrations/SetupGuideTruckIllustration.svg'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks/useFMSAnalytics'
import { TEST_IDS } from '../../../constants'
import { ISetupGuideInfoQuery } from '../graphql/SetupGuideInfo'

const useSetupGuideCardStyles = makeStyles(theme => ({
  card: {
    overflow: 'hidden',
    backgroundColor: brandColors.gray5,
    borderRadius: theme.spacing(1),
    borderColor: brandColors.coolGray5,
    boxShadow: '0px 6px 12px 0px rgba(0, 0, 0, 0.15)',
  },
  cardIllustration: {
    height: '118px',
    '& > img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'bottom left',
    },
  },
  cardContent: {
    padding: `${theme.spacing(3)} ${theme.spacing(1.25)} ${theme.spacing(1.25)}`,
  },
  title: {
    marginBottom: theme.spacing(0.5),
    padding: `0 ${theme.spacing(1.25)}`,
  },
  button: {
    textAlign: 'left',
    width: '100%',
    padding: theme.spacing(1.25),
    border: '0px',
    '& > span': {
      display: 'block',
      width: '100%',
    },
    '&:hover, &:focus': {
      backgroundColor: brandColors.gray4,
    },
  },
  buttonContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
  },
  buttonTitle: {
    color: brandColors.skyBlue3,
  },
  buttonIcons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
}))

export interface ISetupGuideCardProps {
  motorCarriersForUser: ISetupGuideInfoQuery['motorCarriersForUser']
}

const SetupGuideCard = memo<ISetupGuideCardProps>(({ motorCarriersForUser }) => {
  const { trackEvent } = useFMSAnalytics()
  const history = useHistory()
  const classes = useSetupGuideCardStyles()

  const clickHandler = useCallback(() => {
    trackEvent('Homepage', 'HOME_SETUP_GUIDE_CLICKED_CTA', { motorCarriersForUser })
    history.push('/home/carrier-setup')
  }, [history, motorCarriersForUser, trackEvent])

  return (
    <Card dataTest={TEST_IDS.setupGuideCard} className={classes.card} noPadding>
      <div className={classes.cardIllustration}>
        <img src={SetupGuideTruckIllustration} alt='SetupGuideTruckIllustration' />
      </div>
      <div className={classes.cardContent}>
        <div className={classes.title}>
          <Typography variant='h4' component='div' color='secondary'>
            Get started on Transfix
          </Typography>
        </div>
        <Button onClick={clickHandler} className={classes.button} variant='tertiary'>
          <div className={classes.buttonContent}>
            <div>
              <Typography
                variant='button'
                color='primary'
                component='div'
                className={classes.buttonTitle}>
                Get your carrier approved to book loads
              </Typography>
              <Typography variant='body2' color='secondary' component='div'>
                Sign our Broker-Carrier Agreement and submit your carrier packet in under 2 minutes.
              </Typography>
            </div>
            <div className={classes.buttonIcons}>
              <CheckOutlineIcon size='xlarge' color='coolGray4' />
              <RightArrowIcon size='medium' color='coolGray4' />
            </div>
          </div>
        </Button>
      </div>
    </Card>
  )
})

export default SetupGuideCard
